/**
 * Capsule: one individually-decoded message unit within a session container.
 *
 * A capsule reuses the frozen S3 encrypt-then-fragment path (`@fume/core`). The
 * ratchet's single-use per-message key is the content key `K_c`; because it is
 * used exactly once, the fragment layer's fixed all-zero inner nonce stays
 * sound. Each lane pins a distinct fragment `direction`, binding the lane into
 * the per-fragment AEAD associated data (defence-in-depth on top of the lanes'
 * independent key material). No chaff at the capsule layer — traffic-analysis
 * padding is a transport-layer concern, orthogonal to this unit.
 */
import { FRAGMENT_SIZE, encryptAndFragment, parseFragment, serializeFragment, verifyAndReassemble, } from '@fume/core';
const CAPSULE_VERSION = 0x01;
const HEADER_SIZE = 1 /* version */ + 1 /* lane */ + 4 /* seq */ + 4 /* fragCount */;
function laneByte(lane) {
    return lane === 'a2b' ? 0x00 : 0x01;
}
function laneFromByte(b) {
    if (b === 0x00)
        return 'a2b';
    if (b === 0x01)
        return 'b2a';
    throw new Error(`unknown lane byte ${b}`);
}
/** Each lane pins a distinct fragment direction, binding the lane into the AD. */
export function laneDirection(lane) {
    return lane === 'a2b' ? 'send' : 'recv';
}
/**
 * Seal one plaintext message into a capsule under a single-use `capsuleKey`.
 */
export function sealCapsule(plaintext, capsuleKey, lane, seq, transcriptHash, sessionId) {
    const result = encryptAndFragment(plaintext, capsuleKey, transcriptHash, sessionId, laneDirection(lane));
    const fragments = result.realFragments
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((f) => serializeFragment(f));
    return { lane, seq, fragments };
}
/**
 * Open a capsule under its single-use `capsuleKey`, returning the plaintext.
 * Enforces I3 via verifyAndReassemble (AEAD + transcript/session/direction AD).
 */
export function openCapsule(capsule, capsuleKey, transcriptHash, sessionId) {
    const n = capsule.fragments.length;
    const direction = laneDirection(capsule.lane);
    const fragments = capsule.fragments.map((bytes, index) => parseFragment(bytes, { kind: 'real', index, totalCount: n, direction }));
    return verifyAndReassemble(fragments, capsuleKey, transcriptHash, sessionId, direction);
}
/** Serialize a capsule to a single self-describing wire blob. */
export function serializeCapsule(capsule) {
    const n = capsule.fragments.length;
    const out = new Uint8Array(HEADER_SIZE + n * FRAGMENT_SIZE);
    const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
    view.setUint8(0, CAPSULE_VERSION);
    view.setUint8(1, laneByte(capsule.lane));
    view.setUint32(2, capsule.seq, false);
    view.setUint32(6, n, false);
    let off = HEADER_SIZE;
    for (const frag of capsule.fragments) {
        if (frag.length !== FRAGMENT_SIZE) {
            throw new Error('capsule fragment is not FRAGMENT_SIZE bytes');
        }
        out.set(frag, off);
        off += FRAGMENT_SIZE;
    }
    return out;
}
/** Inverse of {@link serializeCapsule}. */
export function parseCapsule(bytes) {
    if (!(bytes instanceof Uint8Array) || bytes.length < HEADER_SIZE) {
        throw new Error('capsule bytes too short');
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const version = view.getUint8(0);
    if (version !== CAPSULE_VERSION) {
        throw new Error(`unknown capsule version ${version}`);
    }
    const lane = laneFromByte(view.getUint8(1));
    const seq = view.getUint32(2, false);
    const n = view.getUint32(6, false);
    if (bytes.length !== HEADER_SIZE + n * FRAGMENT_SIZE) {
        throw new Error('capsule length does not match declared fragment count');
    }
    const fragments = [];
    let off = HEADER_SIZE;
    for (let i = 0; i < n; i += 1) {
        fragments.push(bytes.slice(off, off + FRAGMENT_SIZE));
        off += FRAGMENT_SIZE;
    }
    return { lane, seq, fragments };
}
//# sourceMappingURL=capsule.js.map